import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, Card, Typography, Alert, Result } from 'antd';
import { MailOutlined, HeartOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import authService from '../../services/authService';
import './AuthPages.scss';

const { Title, Text } = Typography;

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const onFinish = async (values) => {
    setLoading(true);
    setError(null);

    try {
      await authService.forgotPassword(values.email);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card-wrapper bounce-in">
            <Card className="auth-card">
              <Result
                status="success"
                title={t('auth.forgotPassword.successTitle')}
                subTitle={t('auth.forgotPassword.successSubtitle')}
                extra={[
                  <Link to="/login" key="login">
                    <Button type="primary" size="large">
                      {t('auth.forgotPassword.backToLogin')}
                    </Button>
                  </Link>
                ]}
              />
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card-wrapper bounce-in">
          <Card className="auth-card">
            <div className="auth-header">
              <div className="logo-section">
                <HeartOutlined className="logo-icon" />
                <Title level={2} className="auth-title">
                  {t('auth.forgotPassword.title')}
                </Title>
                <Text className="auth-subtitle">
                  {t('auth.forgotPassword.subtitle')}
                </Text>
              </div>
            </div>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
                style={{ marginBottom: 24 }}
              />
            )}

            <Form
              form={form}
              name="forgot-password"
              onFinish={onFinish}
              layout="vertical"
              size="large"
              autoComplete="off"
            >
              <Form.Item
                name="email"
                label={t('auth.login.emailLabel')}
                rules={[
                  { required: true, message: t('auth.login.emailRequired') },
                  { type: 'email', message: t('auth.login.emailInvalid') },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder={t('auth.login.emailPlaceholder')}
                  autoComplete="email"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  className="auth-button"
                >
                  {t('auth.forgotPassword.submitButton')}
                </Button>
              </Form.Item>

              <div className="auth-footer">
                <Link to="/login" className="auth-link">
                  <ArrowLeftOutlined /> {t('auth.forgotPassword.backToLogin')}
                </Link>
              </div>
            </Form>
          </Card>

          <div className="auth-decoration">
            <div className="paw-print">🐾</div>
            <div className="paw-print">🐾</div>
            <div className="paw-print">🐾</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
